import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MPDashboardLayout from '../../components/mp/MPDashboardLayout';
import { useTheme } from '../../context/ThemeContext';
import { exercisesApi, platformsApi } from '../../lib/mpApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Slider } from '../../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { toast } from 'sonner';
import { TIMEZONES } from '../../lib/timezone';
import { copyToClipboard } from '../../lib/clipboard';
import {
  Plus, Target, Edit, Trash2, Eye, Copy, Search, Globe, Calendar, Loader2,
  CheckCircle, ArrowRight, ArrowLeft, Users, Radio, Clock, Shield, X, ChevronsUpDown, Check, ArrowUpDown, Filter, ImagePlus,
} from 'lucide-react';
import { mediaApi } from '../../lib/mpApi';

// V14.13.4 — Searchable multi-select for picking simcels/observers per exercise.
// Scales to 100+ users without bloating the wizard.
const UserMultiSelect = ({ users, selectedIds, onChange, placeholder, testIdPrefix }) => {
  const [open, setOpen] = useState(false);
  const selected = users.filter(u => selectedIds.includes(u.user_id));
  const toggle = (id) => {
    onChange(selectedIds.includes(id)
      ? selectedIds.filter(x => x !== id)
      : [...selectedIds, id]);
  };
  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between bg-muted/50 border-input text-foreground font-normal h-9"
            data-testid={`${testIdPrefix}-trigger`}
          >
            <span className="text-xs text-muted-foreground">
              {selected.length === 0 ? placeholder : `${selected.length} selected`}
            </span>
            <ChevronsUpDown className="w-3.5 h-3.5 opacity-50 ml-2 flex-shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[260px] p-0 bg-card border-border" align="start">
          <Command>
            <CommandInput placeholder="Search users..." className="h-9" />
            <CommandList>
              <CommandEmpty>No users found.</CommandEmpty>
              <CommandGroup>
                {users.map(u => {
                  const isSel = selectedIds.includes(u.user_id);
                  return (
                    <CommandItem
                      key={u.user_id}
                      value={`${u.name || ''} ${u.email}`}
                      onSelect={() => toggle(u.user_id)}
                      data-testid={`${testIdPrefix}-option-${u.user_id}`}
                      className="cursor-pointer"
                    >
                      <Check className={`w-4 h-4 mr-2 ${isSel ? 'opacity-100 text-red-500' : 'opacity-0'}`} />
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm truncate">{u.name || u.email}</span>
                        {u.name && <span className="text-[10px] text-muted-foreground truncate">{u.email}</span>}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-chips`}>
          {selected.map(u => (
            <Badge
              key={u.user_id}
              variant="outline"
              className="text-[11px] py-0.5 pl-2 pr-1 border-border bg-muted/50 gap-1"
              data-testid={`${testIdPrefix}-chip-${u.user_id}`}
            >
              <span className="truncate max-w-[120px]">{u.name || u.email}</span>
              <button
                type="button"
                onClick={() => toggle(u.user_id)}
                className="ml-0.5 rounded-full hover:bg-red-500/20 hover:text-red-500 p-0.5"
                aria-label={`Remove ${u.name || u.email}`}
                data-testid={`${testIdPrefix}-chip-remove-${u.user_id}`}
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;


const statusConfig = {
  draft: { label: 'Draft', color: 'bg-muted0/10 text-muted-foreground border-border/20', dot: 'bg-muted-foreground' },
  published: { label: 'Published', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20', dot: 'bg-blue-400' },
  enabled: { label: 'Live', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', dot: 'bg-emerald-400' },
  disabled: { label: 'Disabled', color: 'bg-red-500/10 text-red-400 border-red-500/20', dot: 'bg-red-400' },
};

// V14.13 — Added "Unpublish" action to revert published/enabled/disabled
// exercises back to draft (cascades and unpublishes their posts).
const statusActions = {
  draft: [{ label: 'Publish', action: 'publish', icon: ArrowRight }],
  published: [
    { label: 'Go Live', action: 'enable', icon: Radio },
    { label: 'Unpublish', action: 'unpublish', icon: Shield },
  ],
  enabled: [
    { label: 'Disable', action: 'disable', icon: Shield },
    { label: 'Unpublish', action: 'unpublish', icon: ArrowRight },
  ],
  disabled: [
    { label: 'Re-enable', action: 'enable', icon: Radio },
    { label: 'Unpublish', action: 'unpublish', icon: ArrowRight },
  ],
};

const MPExercisesPage = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [exercises, setExercises] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [emsExercises, setEmsExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [showDialog, setShowDialog] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [saving, setSaving] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '', description: '', start_date: '', end_date: '',
    guest_access: false, selected_platforms: [], platform_widths: [],
    timezone: 'UTC', logo_url: '',
    // V14.13.3 — Per-exercise read/write grants (mirror EMS pattern).
    assigned_controllers: [], assigned_observers: [],
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [mpUsers, setMpUsers] = useState([]);

  // Load MP users (used for controllers/observers pickers in the edit dialog)
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mp/users`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
        if (res.ok) setMpUsers(await res.json());
      } catch { /* noop */ }
    };
    loadUsers();
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [exData, platData, emsData] = await Promise.all([
        exercisesApi.getAll(),
        platformsApi.getAll().catch(() => []),
        exercisesApi.getEmsExercises().catch(() => []),
      ]);
      setExercises(Array.isArray(exData) ? exData : []);
      setPlatforms(Array.isArray(platData) ? platData : []);
      setEmsExercises(Array.isArray(emsData) ? emsData : []);
    } catch { toast.error('Failed to load exercises'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = exercises
    .filter(e => {
      if (filterStatus !== 'all' && e.status !== filterStatus) return false;
      if (search && !e.name?.toLowerCase().includes(search.toLowerCase()) &&
          !e.description?.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      const statusPriority = { enabled: 0, published: 1, draft: 2, disabled: 3 };
      const aPri = statusPriority[a.status] ?? 4;
      const bPri = statusPriority[b.status] ?? 4;
      if (aPri !== bPri) return aPri - bPri;
      const aDate = new Date(a.created_at || a.start_date || 0).getTime();
      const bDate = new Date(b.created_at || b.start_date || 0).getTime();
      return sortOrder === 'newest' ? bDate - aDate : aDate - bDate;
    });

  const openCreate = () => {
    setEditingExercise(null);
    setWizardStep(0);
    setFormData({ name: '', description: '', start_date: '', end_date: '', guest_access: false, selected_platforms: [], platform_widths: [], timezone: 'UTC', linked_ems_exercise_id: '', logo_url: '', assigned_controllers: [], assigned_observers: [] });
    setShowDialog(true);
  };

  const openEdit = (ex) => {
    setEditingExercise(ex);
    setWizardStep(0);
    setFormData({
      name: ex.name || '', description: ex.description || '',
      start_date: ex.start_date || '', end_date: ex.end_date || '',
      guest_access: ex.guest_access || false,
      selected_platforms: ex.selected_platforms || [],
      platform_widths: ex.platform_widths || [],
      timezone: ex.timezone || 'UTC',
      linked_ems_exercise_id: ex.linked_ems_exercise_id || '',
      logo_url: ex.logo_url || '',
      assigned_controllers: ex.assigned_controllers || [],
      assigned_observers: ex.assigned_observers || [],
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      // Strip guest_password from the main update payload; it has its own endpoint.
      const { guest_password, ...mainPayload } = formData;
      let exerciseId;
      if (editingExercise) {
        await exercisesApi.update(editingExercise.exercise_id, mainPayload);
        exerciseId = editingExercise.exercise_id;
        toast.success('Exercise updated');
      } else {
        const created = await exercisesApi.create(mainPayload);
        exerciseId = created.exercise_id;
        toast.success('Exercise created');
      }
      // V14.13 — Sync the guest-link password independently. Empty string
      // clears any existing password.
      if (formData.guest_access && guest_password !== undefined && guest_password !== null) {
        try { await exercisesApi.setGuestPassword(exerciseId, guest_password); }
        catch (e) { toast.error(e.response?.data?.detail || 'Password not saved'); }
      }
      setShowDialog(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDuplicate = async (ex) => {
    if (!window.confirm(`Duplicate "${ex.name}" with all its posts? The copy will start as a draft so you can edit it before publishing.`)) return;
    try {
      const dup = await exercisesApi.duplicate(ex.exercise_id);
      toast.success('Exercise duplicated (with all posts). Opening edit…');
      // Refresh list then open edit modal on the new exercise
      await loadData();
      openEdit(dup);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Duplicate failed');
    }
  };

  const handleDelete = async (ex) => {
    if (!window.confirm(`Delete "${ex.name}"?`)) return;
    try {
      await exercisesApi.delete(ex.exercise_id);
      toast.success('Exercise deleted');
      loadData();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Delete failed';
      toast.error(detail);
    }
  };

  const handleStatusChange = async (ex, action) => {
    if (action === 'unpublish') {
      const publishedPostCount = ex.published_post_count || 0;
      const msg = publishedPostCount > 0
        ? `Unpublish "${ex.name}"? This will also unpublish all ${publishedPostCount} published posts.`
        : `Unpublish "${ex.name}"? It will return to draft status.`;
      if (!window.confirm(msg)) return;
    }
    try {
      if (action === 'publish') await exercisesApi.publish(ex.exercise_id);
      else if (action === 'unpublish') await exercisesApi.unpublish(ex.exercise_id);
      else if (action === 'enable') await exercisesApi.enable(ex.exercise_id);
      else if (action === 'disable') await exercisesApi.disable(ex.exercise_id);
      toast.success(`Exercise ${action}ed`);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || `Failed to ${action}`); }
  };

  const togglePlatform = (platName) => {
    setFormData(prev => {
      const selected = prev.selected_platforms.includes(platName)
        ? prev.selected_platforms.filter(p => p !== platName)
        : [...prev.selected_platforms, platName];
      return { ...prev, selected_platforms: selected };
    });
  };

  const updatePlatformWidth = (platName, width) => {
    setFormData(prev => {
      const existing = prev.platform_widths.filter(pw => pw.platform !== platName);
      return { ...prev, platform_widths: [...existing, { platform: platName, width }] };
    });
  };

  const getWidthForPlatform = (platName) => {
    const found = formData.platform_widths.find(pw => pw.platform === platName);
    return found ? found.width : Math.floor(100 / Math.max(formData.selected_platforms.length, 1));
  };

  const wizardSteps = [
    { label: 'Details', icon: Target },
    { label: 'Schedule', icon: Calendar },
    { label: 'Platforms', icon: Globe },
  ];

  return (
    <MPDashboardLayout title="Exercises">
      <div className="space-y-6" data-testid="mp-exercises-page">
        {/* Toolbar */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search exercises..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-muted border-input text-foreground placeholder:text-muted-foreground" data-testid="mp-exercises-search" />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[130px] bg-muted border-input text-foreground" data-testid="mp-exercises-filter-status">
              <Filter className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="enabled">Live</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="disabled">Disabled</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortOrder} onValueChange={setSortOrder}>
            <SelectTrigger className="w-[130px] bg-muted border-input text-foreground" data-testid="mp-exercises-sort">
              <ArrowUpDown className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest First</SelectItem>
              <SelectItem value="oldest">Oldest First</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={openCreate} className="bg-red-600 hover:bg-red-700 text-white" data-testid="mp-create-exercise-btn">
            <Plus className="w-4 h-4 mr-2" /> New Exercise
          </Button>
        </div>

        {/* Stats summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total', value: exercises.length, color: 'text-foreground' },
            { label: 'Live', value: exercises.filter(e => e.status === 'enabled').length, color: 'text-emerald-500' },
            { label: 'Draft', value: exercises.filter(e => e.status === 'draft').length, color: 'text-muted-foreground' },
            { label: 'Disabled', value: exercises.filter(e => e.status === 'disabled').length, color: 'text-red-500' },
          ].map(s => (
            <div key={s.label} className="bg-card border border-border rounded-lg px-4 py-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Exercises List */}
        {loading ? (
          <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Target className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No exercises found</p>
            <Button onClick={openCreate} variant="outline" className="mt-4 border-border text-foreground">
              <Plus className="w-4 h-4 mr-2" /> Create your first exercise
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(ex => {
              const config = statusConfig[ex.status] || statusConfig.draft;
              const actions = statusActions[ex.status] || [];
              return (
                <Card key={ex.exercise_id} className="bg-card border-border hover:border-primary/30 transition-colors cursor-pointer" data-testid={`mp-exercise-${ex.exercise_id}`}
                  onClick={() => navigate(`/mp/exercises/${ex.exercise_id}`)}>
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      {/* Logo / Status indicator */}
                      <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 relative overflow-hidden border border-border">
                        {ex.logo_url ? (
                          <img src={ex.logo_url.startsWith('/') ? `${BACKEND_URL}${ex.logo_url}` : ex.logo_url}
                            alt="" className="w-full h-full object-contain p-1" />
                        ) : (
                          <Target className="w-5 h-5 text-muted-foreground" />
                        )}
                        <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ${config.dot} ring-2 ring-background`} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="font-semibold text-foreground">{ex.name}</h3>
                          <Badge className={`text-[10px] border ${config.color}`}>{config.label}</Badge>
                          {ex.guest_access && <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-500">Guest</Badge>}
                          {ex.guest_password_set && <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-500" data-testid={`pw-protected-${ex.exercise_id}`}>🔒 PW</Badge>}
                          {ex.linked_ems_exercise_id && <Badge variant="outline" className="text-[10px] border-blue-500/30 text-blue-500">EMS Linked</Badge>}
                        </div>
                        {ex.description && <p className="text-sm text-muted-foreground line-clamp-1 mb-2">{ex.description}</p>}

                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{ex.selected_platforms?.length || 0} platforms</span>
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{ex.timezone || 'UTC'}</span>
                          {ex.start_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(ex.start_date).toLocaleDateString()}</span>}
                          {ex.guest_token && (
                            <button onClick={(e) => { e.stopPropagation(); copyToClipboard(`${BACKEND_URL}/guest/${ex.guest_token}`).then(() => toast.success('Guest link copied')); }}
                              className="flex items-center gap-1 text-red-500 hover:text-red-400 transition">
                              <Copy className="w-3 h-3" /> Copy Guest Link
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
                        {actions.map(a => (
                          <Button key={a.action} variant="outline" size="sm" onClick={() => handleStatusChange(ex, a.action)}
                            className="border-border text-foreground text-xs h-8">
                            <a.icon className="w-3 h-3 mr-1" /> {a.label}
                          </Button>
                        ))}
                        {ex.status === 'enabled' && ex.guest_token && (
                          <Button variant="outline" size="sm" onClick={() => window.open(`/guest/${ex.guest_token}`, '_blank')}
                            className="border-border text-foreground text-xs h-8">
                            <Eye className="w-3 h-3 mr-1" /> View
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEdit(ex); }} className="text-muted-foreground hover:text-foreground h-8 w-8 p-0" data-testid={`mp-exercise-edit-${ex.exercise_id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDuplicate(ex); }} className="text-muted-foreground hover:text-foreground h-8 w-8 p-0" data-testid={`mp-exercise-duplicate-${ex.exercise_id}`} title="Duplicate (copies all posts)">
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(ex); }} className="text-muted-foreground hover:text-red-500 h-8 w-8 p-0" data-testid={`mp-exercise-delete-${ex.exercise_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog — Wizard */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card border-border text-foreground">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-foreground">
              {editingExercise ? 'Edit Exercise' : 'Create Exercise'}
            </DialogTitle>
          </DialogHeader>

          {/* Wizard Steps */}
          <div className="flex items-center gap-1 mb-6">
            {wizardSteps.map((step, i) => (
              <React.Fragment key={step.label}>
                <button onClick={() => setWizardStep(i)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                    i === wizardStep ? 'bg-red-600/20 text-red-500 font-medium' :
                    i < wizardStep ? 'bg-emerald-600/10 text-emerald-500' : 'text-muted-foreground'
                  }`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                    i === wizardStep ? 'bg-red-600 text-white' :
                    i < wizardStep ? 'bg-emerald-600 text-white' : 'bg-muted text-muted-foreground'
                  }`}>
                    {i < wizardStep ? <CheckCircle className="w-3.5 h-3.5" /> : i + 1}
                  </div>
                  <span className="hidden sm:inline">{step.label}</span>
                </button>
                {i < wizardSteps.length - 1 && <div className={`flex-1 h-px ${i < wizardStep ? 'bg-emerald-600' : 'bg-border'}`} />}
              </React.Fragment>
            ))}
          </div>

          {/* Step 1: Details */}
          {wizardStep === 0 && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Exercise Name</Label>
                <Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g. Oil Spill Response Drill 2026"
                  className="bg-muted border-input placeholder:text-muted-foreground"
                  data-testid="mp-exercise-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Description</Label>
                <Textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})}
                  placeholder="Brief description of the exercise scenario..."
                  rows={3} className="bg-muted border-input placeholder:text-muted-foreground" />
              </div>
              {/* Logo Upload */}
              <div className="bg-muted rounded-lg p-4 border border-border space-y-3">
                <div>
                  <p className="text-sm font-medium text-foreground">Exercise Logo</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Upload a client or exercise logo (shown in exercise list, dashboard and live display)</p>
                </div>
                <div className="flex items-center gap-4">
                  {formData.logo_url ? (
                    <div className="relative group">
                      <img src={formData.logo_url.startsWith('/') ? `${BACKEND_URL}${formData.logo_url}` : formData.logo_url}
                        alt="Exercise logo" className="w-16 h-16 rounded-lg object-contain border border-border bg-white" />
                      <button type="button" onClick={() => setFormData({...formData, logo_url: ''})}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-lg border-2 border-dashed border-border flex items-center justify-center bg-muted/50">
                      <ImagePlus className="w-5 h-5 text-muted-foreground" />
                    </div>
                  )}
                  <Button type="button" variant="outline" size="sm" disabled={uploadingLogo}
                    className="border-border text-foreground"
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file'; input.accept = 'image/*';
                      input.onchange = async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setUploadingLogo(true);
                        try {
                          const result = await mediaApi.upload(file);
                          setFormData(prev => ({...prev, logo_url: result.file_url}));
                          toast.success('Logo uploaded');
                        } catch { toast.error('Upload failed'); }
                        finally { setUploadingLogo(false); }
                      };
                      input.click();
                    }}
                    data-testid="mp-exercise-logo-upload">
                    {uploadingLogo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ImagePlus className="w-4 h-4 mr-2" />}
                    {formData.logo_url ? 'Change' : 'Upload'} Logo
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between bg-muted rounded-lg p-4 border border-border">
                <div>
                  <p className="text-sm font-medium text-foreground">Guest Access</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Allow external participants via a shareable link</p>
                </div>
                <Switch checked={formData.guest_access} onCheckedChange={v => setFormData({...formData, guest_access: v})}
                  data-testid="mp-exercise-guest-toggle" />
              </div>

              {/* V14.13 — Optional guest-link password. Shared single password. */}
              {formData.guest_access && (
                <div className="bg-muted rounded-lg p-4 border border-border space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-foreground">Guest Link Password (optional)</p>
                    {editingExercise?.guest_password_set && (
                      <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-500 rounded-sm">
                        Currently protected
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {editingExercise?.guest_password_set
                      ? 'Type a new password to change it, or click Remove Password to open the link to anyone.'
                      : 'Leave blank for no password. Anyone with the link gets in.'}
                  </p>
                  <Input
                    type="password"
                    value={formData.guest_password || ''}
                    onChange={(e) => setFormData({...formData, guest_password: e.target.value})}
                    placeholder={editingExercise?.guest_password_set ? '••••••••' : 'No password set'}
                    className="bg-muted border-input"
                    data-testid="mp-exercise-guest-password"
                    autoComplete="new-password"
                  />
                  {editingExercise?.guest_password_set && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-sm border-red-500/40 text-red-500 hover:bg-red-500/10 hover:text-red-500"
                      data-testid="mp-exercise-remove-guest-password"
                      onClick={async () => {
                        if (!window.confirm('Remove the guest-link password? Anyone with the link will be able to view this exercise without a password.')) return;
                        try {
                          await exercisesApi.setGuestPassword(editingExercise.exercise_id, '');
                          toast.success('Guest password removed');
                          setFormData(prev => ({ ...prev, guest_password: '' }));
                          setEditingExercise(prev => prev ? { ...prev, guest_password_set: false } : prev);
                          loadData();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Failed to remove password');
                        }
                      }}
                    >
                      <X className="w-3.5 h-3.5 mr-1.5" />
                      Remove Password
                    </Button>
                  )}
                </div>
              )}
              {/* V14.13.4 — Per-exercise read/write grants. Mirrors EMS.
                  Searchable dropdown scales beyond 100 users vs. checkbox lists. */}
              <div className="bg-muted rounded-lg p-4 border border-border space-y-3">
                <div>
                  <p className="text-sm font-medium text-foreground">Exercise Access</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Grant per-exercise read or write. Empty = global roles only.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Controllers (Write)</Label>
                    <UserMultiSelect
                      users={mpUsers.filter(u => (u.mp_roles || []).includes('simcel') || (u.roles || []).includes('simcel') || u.role === 'simcel')}
                      selectedIds={formData.assigned_controllers}
                      onChange={(ids) => setFormData(prev => ({ ...prev, assigned_controllers: ids }))}
                      placeholder="Add simcels..."
                      testIdPrefix="mp-ctrl"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Observers (Read)</Label>
                    <UserMultiSelect
                      users={mpUsers.filter(u => (u.mp_roles || []).includes('observer') || (u.roles || []).includes('observer') || u.role === 'observer')}
                      selectedIds={formData.assigned_observers}
                      onChange={(ids) => setFormData(prev => ({ ...prev, assigned_observers: ids }))}
                      placeholder="Add observers..."
                      testIdPrefix="mp-obs"
                    />
                  </div>
                </div>
              </div>

              {/* EMS Exercise Linking */}
              <div className="bg-muted rounded-lg p-4 border border-border space-y-2">
                <div>
                  <p className="text-sm font-medium text-foreground">Link to EMS Exercise</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Published MP posts will appear as events in the linked EMS exercise timeline</p>
                </div>
                <Select value={formData.linked_ems_exercise_id || 'none'} onValueChange={v => setFormData({...formData, linked_ems_exercise_id: v === 'none' ? '' : v})}>
                  <SelectTrigger className="bg-muted border-input" data-testid="mp-exercise-ems-link">
                    <SelectValue placeholder="No link" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No link (standalone)</SelectItem>
                    {emsExercises.map(ex => (
                      <SelectItem key={ex.exercise_id} value={ex.exercise_id}>
                        {ex.name} ({ex.status})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Step 2: Schedule */}
          {wizardStep === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Start Date & Time</Label>
                  <Input type="datetime-local" value={formData.start_date}
                    onChange={e => setFormData({...formData, start_date: e.target.value})}
                    className="bg-muted border-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">End Date & Time</Label>
                  <Input type="datetime-local" value={formData.end_date}
                    onChange={e => setFormData({...formData, end_date: e.target.value})}
                    className="bg-muted border-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Timezone</Label>
                <Select value={formData.timezone} onValueChange={v => setFormData({...formData, timezone: v})}>
                  <SelectTrigger className="bg-muted border-input"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIMEZONES.map(tz => <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {formData.start_date && formData.end_date && (
                <div className="bg-muted rounded-lg p-4 border border-border">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Duration</p>
                  <p className="text-sm text-foreground">
                    {(() => {
                      const start = new Date(formData.start_date);
                      const end = new Date(formData.end_date);
                      const diffMs = end - start;
                      const days = Math.floor(diffMs / 86400000);
                      const hours = Math.floor((diffMs % 86400000) / 3600000);
                      return `${days > 0 ? days + ' day(s) ' : ''}${hours} hour(s)`;
                    })()}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Platforms */}
          {wizardStep === 2 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Select Platforms</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {platforms.map(p => {
                    const selected = formData.selected_platforms.includes(p.name);
                    return (
                      <button key={p.platform_id} onClick={() => togglePlatform(p.name)}
                        className={`flex items-center gap-3 p-3 rounded-lg border text-sm transition-all text-left ${
                          selected ? 'border-red-500/50 bg-red-600/10 ring-1 ring-red-500/20' : 'border-border bg-muted hover:bg-accent'
                        }`}
                        data-testid={`mp-platform-toggle-${p.name}`}>
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                          style={{ backgroundColor: p.color || '#666' }}>
                          {p.display_name?.charAt(0)}
                        </div>
                        <div className="min-w-0">
                          <p className={`font-medium truncate ${selected ? 'text-foreground' : 'text-foreground/80'}`}>{p.display_name}</p>
                          <p className="text-[10px] text-muted-foreground">{p.category}</p>
                        </div>
                        {selected && <CheckCircle className="w-4 h-4 text-red-400 ml-auto flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {formData.selected_platforms.length > 0 && (
                <div className="space-y-3 bg-muted rounded-lg p-4 border border-border">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Display Width per Platform</Label>
                  {formData.selected_platforms.map(pName => {
                    const plat = platforms.find(p => p.name === pName);
                    return (
                      <div key={pName} className="flex items-center gap-3">
                        <span className="text-sm w-24 truncate text-foreground/80">{plat?.display_name || pName}</span>
                        <Slider value={[getWidthForPlatform(pName)]} onValueChange={([v]) => updatePlatformWidth(pName, v)}
                          min={10} max={80} step={5} className="flex-1" />
                        <span className="text-sm w-12 text-right text-muted-foreground font-mono">{getWidthForPlatform(pName)}%</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex justify-between gap-2 pt-4 border-t border-border">
            <div>
              {wizardStep > 0 && (
                <Button variant="outline" onClick={() => setWizardStep(wizardStep - 1)}
                  className="border-border text-foreground">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowDialog(false)}
                className="border-border text-foreground">Cancel</Button>
              {wizardStep < 2 ? (
                <Button onClick={() => {
                  if (wizardStep === 0 && !formData.name.trim()) { toast.error('Name is required'); return; }
                  setWizardStep(wizardStep + 1);
                }} className="bg-red-600 hover:bg-red-700 text-white">
                  Next <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button onClick={handleSave} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="mp-exercise-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {editingExercise ? 'Update' : 'Create'} Exercise
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MPDashboardLayout>
  );
};

export default MPExercisesPage;
